export const SITE = {
  name: "Aditi Sah",
  role: "Makeup Artist",
  location: "Kathmandu, Nepal",
  tagline: "The First Look",
  intro:
    "A single debut makeup, taken from bare skin to final finish. My first full face, and the first one I could call perfect.",
  /** Nepal country code (977) + 9820730567, digits only as wa.me requires. */
  whatsappNumber: "9779820730567",
  whatsappMessage:
    "Hi Aditi! I saw your website and I'd love to book a makeup session.",
  instagramHandle: "",
} as const;

export function whatsappLink(message: string = SITE.whatsappMessage) {
  return `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(message)}`;
}

export function instagramLink(handle: string) {
  return `https://instagram.com/${handle.replace(/^@/, "")}`;
}

export type Service = {
  name: string;
  detail: string;
  /** Display price. PLACEHOLDER — replace with real figures before launch. */
  price: string;
  duration: string;
  includes: string[];
};

export const SERVICES: Service[] = [
  {
    name: "Bridal",
    detail: "The full bridal trial and wedding-day look, tailored to your skin and your dress.",
    price: "From ₹12,000",
    duration: "2.5 hrs",
    includes: ["Trial session", "Day-of application", "Lash & lip longevity", "Travel within city"],
  },
  {
    name: "Party & Events",
    detail: "Dinner, cocktail, reception. Camera-ready and long-wearing, from understated to full glam.",
    price: "From ₹4,500",
    duration: "90 min",
    includes: ["Skin prep", "Full colour & sculpt", "Lashes", "Setting spray"],
  },
  {
    name: "Editorial & Portfolio",
    detail: "Creative direction and precise execution for shoots, campaigns and portfolio work.",
    price: "By project",
    duration: "Half day+",
    includes: ["Look concepting", "On-set touch-ups", "Multiple looks", "Shot-ready finish"],
  },
  {
    name: "Everyday Glamour",
    detail: "A polished, natural finish that looks like you, only better — in about an hour.",
    price: "From ₹2,500",
    duration: "60 min",
    includes: ["Primer & base", "Brows", "Lash curl & mascara", "Balm finish"],
  },
];

export type Testimonial = {
  quote: string;
  name: string;
  context: string;
};

/**
 * PLACEHOLDER CONTENT. These are not real reviews — replace every entry with
 * genuine, permission-granted feedback before the site goes live.
 */
export const TESTIMONIALS: Testimonial[] = [
  {
    quote: "Placeholder review — swap for a real client quote about how the look held.",
    name: "Client name",
    context: "Bridal",
  },
  {
    quote: "Placeholder review — swap for a real client quote about the finish and comfort.",
    name: "Client name",
    context: "Party & Events",
  },
  {
    quote: "Placeholder review — swap for a real client quote about punctuality and professionalism.",
    name: "Client name",
    context: "Editorial",
  },
  {
    quote: "Placeholder review — swap for a real client quote about how natural the skin looked.",
    name: "Client name",
    context: "Everyday Glamour",
  },
];

export type NavItem = {
  id: string;
  label: string;
  short: string;
  /**
   * `href` targets a section on the home page. Items flagged `route` leave the
   * home page entirely and navigate to a standalone page, so they must be
   * rendered as a real link (no scroll-spy, no in-page anchor).
   */
  href: string;
  action?: "book";
  route?: boolean;
};

/** Drives both the desktop header nav and the mobile bottom tab bar. */
export const NAV: NavItem[] = [
  { id: "look", label: "The Look", short: "Look", href: "#look" },
  { id: "transformation", label: "Before & After", short: "Compare", href: "#transformation" },
  { id: "art", label: "The Art", short: "Art", href: "/art", route: true },
  { id: "ad", label: "Ad", short: "Ad", href: "/ad", route: true },
  { id: "services", label: "Services", short: "Services", href: "#services" },
  { id: "book", label: "Book", short: "Book", href: "#book", action: "book" },
];

/** Standalone pages reachable from the nav, beyond `/` and `/admin`. */
export const PAGES = ["/art", "/ad"] as const;
export type PageRoute = (typeof PAGES)[number];

export function normalisePath(pathname: string): PageRoute | "/" | "/admin" {
  const clean = pathname.replace(/\/+$/, "");
  if (clean === "/admin" || clean.startsWith("/admin/")) return "/admin";
  if (clean === "/art" || clean.startsWith("/art/")) return "/art";
  if (clean === "/ad" || clean.startsWith("/ad/")) return "/ad";
  return "/";
}

/**
 * The craft of the look, broken into named movements. Presented as art rather
 * than a product list, so each item reads like a chapter.
 */
export const CRAFT = [
  {
    title: "The Ground",
    body: "Every painting needs a prepared surface. Cleanse, hydrate, prime — until the skin stops asking for attention and starts holding colour.",
    medium: "Skin as canvas",
  },
  {
    title: "The Wash",
    body: "Colour laid in thin, translucent passes. No single layer is allowed to be seen; the finish only exists as the sum of everything underneath it.",
    medium: "Thin layers",
  },
  {
    title: "The Line",
    body: "Brows, liner, lash — the drawing. One confident stroke, then soften it until it reads as structure rather than as marks.",
    medium: "Precision",
  },
  {
    title: "The Signature",
    body: "One shade, placed with intent, and then the final light. It is the part everyone remembers, and the part that has to be right.",
    medium: "The finishing note",
  },
] as const;

export const PROCESS_STEPS = [
  ["01", "Prep & prime", "Cleanse, hydrate, prime — a flawless canvas."],
  ["02", "The base", "Layered in thin passes, blended until seamless."],
  ["03", "Brows & eyes", "Defined and lifted, kept soft, never heavy."],
  ["04", "Lips & finish", "The final shade, set, and photographed."],
] as const;
